import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import {
  Smartphone, Download, Bell, BellRing, Wifi, WifiOff, Shield,
  Zap, RefreshCw, ChevronDown, ChevronRight, Monitor, Apple,
  Chrome, FileText, ShoppingCart, Globe, ArrowRight, CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: 'easeOut' } }),
};

const MobileAppPage = () => {
  const { t, language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState('android');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };

  const platforms = [
    { id: 'android', label: 'Android', icon: Chrome },
    { id: 'ios', label: 'iOS', icon: Apple },
    { id: 'desktop', label: 'Desktop', icon: Monitor },
  ];

  const installSteps = {
    android: [
      { step: 1, text: t('mobile_android_step1') || 'Open Chrome and go to vendfactura.es' },
      { step: 2, text: t('mobile_android_step2') || 'Log in to your account' },
      { step: 3, text: t('mobile_android_step3') || 'Tap the three-dot menu (top right)' },
      { step: 4, text: t('mobile_android_step4') || 'Select "Add to Home Screen" or "Install App"' },
      { step: 5, text: t('mobile_android_step5') || 'Tap "Install" to confirm' },
      { step: 6, text: t('mobile_android_step6') || 'VendFactura icon appears on your home screen!' },
    ],
    ios: [
      { step: 1, text: t('mobile_ios_step1') || 'Open Safari (must be Safari, not Chrome)' },
      { step: 2, text: t('mobile_ios_step2') || 'Go to vendfactura.es and log in' },
      { step: 3, text: t('mobile_ios_step3') || 'Tap the Share button (square with arrow)' },
      { step: 4, text: t('mobile_ios_step4') || 'Scroll down and tap "Add to Home Screen"' },
      { step: 5, text: t('mobile_ios_step5') || 'Tap "Add" to confirm' },
      { step: 6, text: t('mobile_ios_step6') || 'VendFactura icon appears on your home screen!' },
    ],
    desktop: [
      { step: 1, text: t('mobile_desktop_step1') || 'Open Chrome or Edge on your computer' },
      { step: 2, text: t('mobile_desktop_step2') || 'Go to vendfactura.es and log in' },
      { step: 3, text: t('mobile_desktop_step3') || 'Click the install icon in the address bar' },
      { step: 4, text: t('mobile_desktop_step4') || 'Click "Install" in the popup' },
      { step: 5, text: t('mobile_desktop_step5') || 'VendFactura opens as a standalone desktop app!' },
    ],
  };

  const features = [
    {
      icon: WifiOff,
      title: t('mobile_feat_offline_title') || 'Works Offline',
      desc: t('mobile_feat_offline_desc') || 'Process sales and create invoices without internet. Data syncs automatically when you reconnect.',
      color: 'bg-blue-500/10 text-blue-500',
    },
    {
      icon: BellRing,
      title: t('mobile_feat_push_title') || 'Push Notifications',
      desc: t('mobile_feat_push_desc') || 'Get notified about payment reminders, support ticket replies, and important account updates.',
      color: 'bg-amber-500/10 text-amber-500',
    },
    {
      icon: Zap,
      title: t('mobile_feat_fast_title') || 'Fast & Lightweight',
      desc: t('mobile_feat_fast_desc') || 'No app store download. Installs in seconds, uses minimal storage, and loads instantly.',
      color: 'bg-emerald-500/10 text-emerald-500',
    },
    {
      icon: RefreshCw,
      title: t('mobile_feat_update_title') || 'Always Up to Date',
      desc: t('mobile_feat_update_desc') || 'No manual updates needed. You always have the latest version automatically.',
      color: 'bg-purple-500/10 text-purple-500',
    },
    {
      icon: Shield,
      title: t('mobile_feat_secure_title') || 'Secure & Encrypted',
      desc: t('mobile_feat_secure_desc') || 'Runs over HTTPS with full data encryption. Your business data is always protected.',
      color: 'bg-red-500/10 text-red-500',
    },
    {
      icon: Globe,
      title: t('mobile_feat_cross_title') || 'Cross-Platform',
      desc: t('mobile_feat_cross_desc') || 'Works on Android, iOS, Windows, macOS, and Linux. One app for all your devices.',
      color: 'bg-cyan-500/10 text-cyan-500',
    },
  ];

  const faqs = [
    {
      q: t('mobile_faq1_q') || 'What is a PWA?',
      a: t('mobile_faq1_a') || 'A Progressive Web App (PWA) is a website that works like a native app. It can be installed on your device, work offline, and send push notifications — without downloading from an app store.',
    },
    {
      q: t('mobile_faq2_q') || 'Is it different from a regular app?',
      a: t('mobile_faq2_a') || 'Functionally, no. The VendFactura PWA has all the same features as a native app: offline mode, push notifications, home screen icon, and full-screen experience. The only difference is you install it from your browser.',
    },
    {
      q: t('mobile_faq3_q') || 'Does it work on iPhone?',
      a: t('mobile_faq3_a') || 'Yes! On iPhone and iPad, open vendfactura.es in Safari, tap the Share button, then "Add to Home Screen". Note: push notifications on iOS require iOS 16.4 or later.',
    },
    {
      q: t('mobile_faq4_q') || 'What works offline?',
      a: t('mobile_faq4_a') || 'Point of Sale (cash payments), invoice creation, product browsing, and all navigation. Sales and invoices sync automatically when you reconnect. Card payments require internet.',
    },
    {
      q: t('mobile_faq5_q') || 'How do I get push notifications?',
      a: t('mobile_faq5_a') || 'After installing, go to your Dashboard and click "Enable" on the Push Notifications toggle. Allow notifications when your browser asks. You will receive alerts for payment reminders and support ticket replies.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0f172a]" data-testid="mobile-app-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e3a5f]/30 to-transparent" />
        <div className="absolute top-20 right-10 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-6">
                <Smartphone className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">
                  {t('mobile_badge') || 'Progressive Web App'}
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                {t('mobile_hero_title') || 'VendFactura on Your Phone'}
              </h1>
              <p className="text-lg text-slate-400 mb-8 max-w-lg">
                {t('mobile_hero_subtitle') || 'Install VendFactura as a mobile app. Full POS, invoicing, and VeriFactu compliance — right in your pocket. No app store needed.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={deferredPrompt ? handleInstall : () => {
                    document.getElementById('install-guide')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-emerald-500/25"
                  data-testid="mobile-install-btn"
                >
                  <Download className="w-5 h-5" />
                  {deferredPrompt
                    ? (t('mobile_install_btn') || 'Install Now')
                    : (t('mobile_install_guide_btn') || 'How to Install')}
                </button>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-6 py-3 rounded-lg font-semibold transition-all"
                  data-testid="mobile-login-btn"
                >
                  <ArrowRight className="w-5 h-5" />
                  {t('mobile_cta_login') || 'Log In to Your Account'}
                </Link>
              </div>
            </motion.div>

            {/* Phone Mockup */}
            <motion.div initial="hidden" animate="visible" custom={0.3} variants={fadeUp}
              className="hidden lg:flex justify-center"
            >
              <div className="relative">
                <div className="w-[280px] h-[560px] bg-[#1a1a2e] rounded-[3rem] border-4 border-slate-700 shadow-2xl overflow-hidden relative">
                  {/* Phone notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-[#0f172a] rounded-b-2xl z-10" />
                  {/* Screen content */}
                  <div className="h-full bg-gradient-to-b from-[#1e3a5f] to-[#0f172a] pt-10 px-4">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm">VendFactura</div>
                        <div className="text-emerald-400 text-xs">POS & Invoicing</div>
                      </div>
                    </div>
                    {/* Mini dashboard cards */}
                    <div className="space-y-3">
                      <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                        <div className="text-slate-400 text-[10px] mb-1">Today's Sales</div>
                        <div className="text-white font-bold text-lg">1,247.50</div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                          <div className="text-slate-400 text-[10px] mb-1">Invoices</div>
                          <div className="text-white font-bold">24</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/5">
                          <div className="text-slate-400 text-[10px] mb-1">Products</div>
                          <div className="text-white font-bold">156</div>
                        </div>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Wifi className="w-3 h-3 text-emerald-400" />
                          <span className="text-emerald-400 text-[10px] font-medium">Online - All synced</span>
                        </div>
                      </div>
                    </div>
                    {/* Bottom nav mockup */}
                    <div className="absolute bottom-0 left-0 right-0 bg-[#0f172a]/90 backdrop-blur-sm border-t border-white/5 px-4 py-3">
                      <div className="flex justify-around">
                        {['Home', 'POS', 'Invoices', 'More'].map((l, i) => (
                          <div key={l} className="flex flex-col items-center">
                            <div className={`w-1.5 h-1.5 rounded-full mb-1 ${i === 0 ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                            <span className={`text-[9px] ${i === 0 ? 'text-emerald-400' : 'text-slate-500'}`}>{l}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Glow effect */}
                <div className="absolute -inset-4 bg-emerald-500/10 rounded-[4rem] blur-2xl -z-10" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-[#0d1321]" data-testid="mobile-features-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('mobile_features_title') || 'Everything You Need, On Any Device'}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {t('mobile_features_subtitle') || 'The VendFactura PWA brings the full power of our platform to your pocket.'}
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.08} variants={fadeUp}
                className="bg-white/[0.03] border border-white/5 rounded-xl p-6 hover:bg-white/[0.06] transition-colors group"
              >
                <div className={`w-12 h-12 rounded-xl ${feat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feat.icon className="w-6 h-6" />
                </div>
                <h3 className="text-white font-semibold mb-2">{feat.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feat.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Guide */}
      <section id="install-guide" className="py-20" data-testid="mobile-install-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('mobile_install_title') || 'Install in 30 Seconds'}
            </h2>
            <p className="text-slate-400">
              {t('mobile_install_subtitle') || 'Choose your device and follow the simple steps below.'}
            </p>
          </motion.div>

          {/* Platform Tabs */}
          <div className="flex justify-center gap-2 mb-10">
            {platforms.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveTab(p.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === p.id
                    ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`install-tab-${p.id}`}
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </button>
            ))}
          </div>

          {/* Steps */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/[0.03] border border-white/10 rounded-2xl p-8"
          >
            <div className="space-y-0">
              {installSteps[activeTab].map((s, i) => (
                <div key={s.step} className="flex items-start gap-4 relative">
                  {/* Connector line */}
                  {i < installSteps[activeTab].length - 1 && (
                    <div className="absolute left-[17px] top-10 w-0.5 h-[calc(100%-8px)] bg-slate-700" />
                  )}
                  <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0 z-10">
                    <span className="text-emerald-400 text-sm font-bold">{s.step}</span>
                  </div>
                  <div className="pb-8">
                    <p className="text-white text-sm leading-relaxed pt-1.5">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* iOS note */}
            {activeTab === 'ios' && (
              <div className="mt-2 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                <p className="text-amber-400/80 text-xs">
                  {t('mobile_ios_note') || 'Important: On iOS, PWAs must be installed from Safari. Chrome and other browsers on iOS do not support PWA installation.'}
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Push Notifications Section */}
      <section className="py-20 bg-[#0d1321]" data-testid="mobile-push-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-1.5 mb-4">
                <Bell className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">
                  {t('mobile_push_badge') || 'Push Notifications'}
                </span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('mobile_push_title') || 'Never Miss a Payment'}
              </h2>
              <p className="text-slate-400 mb-6">
                {t('mobile_push_desc') || 'Get instant alerts on your phone when payment reminders are sent, support tickets are answered, or important events need your attention.'}
              </p>
              <div className="space-y-3">
                {[
                  t('mobile_push_item1') || 'Payment reminder confirmations',
                  t('mobile_push_item2') || 'Support ticket replies',
                  t('mobile_push_item3') || 'Account and security alerts',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0.2} variants={fadeUp}>
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <h3 className="text-white font-semibold mb-4">{t('mobile_push_howto') || 'How to Enable'}</h3>
                <div className="space-y-4">
                  {[
                    t('mobile_push_step1') || 'Go to your Shop Dashboard',
                    t('mobile_push_step2') || 'Find the "Push Notifications" toggle',
                    t('mobile_push_step3') || 'Click "Enable" and allow when browser asks',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-7 h-7 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                        <span className="text-amber-400 text-xs font-bold">{i + 1}</span>
                      </div>
                      <p className="text-slate-300 text-sm pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Offline Mode Section */}
      <section className="py-20" data-testid="mobile-offline-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0.2} variants={fadeUp}
              className="order-2 md:order-1"
            >
              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-400 text-sm font-medium">{t('mobile_offline_status') || 'Offline Mode Active'}</span>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: ShoppingCart, label: t('mobile_offline_pos') || 'Point of Sale', status: t('mobile_offline_available') || 'Available' },
                    { icon: FileText, label: t('mobile_offline_invoices') || 'Invoice Creation', status: t('mobile_offline_available') || 'Available' },
                    { icon: RefreshCw, label: t('mobile_offline_sync') || 'Auto Sync', status: t('mobile_offline_waiting') || 'Waiting for connection' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/[0.03] rounded-lg px-4 py-3">
                      <div className="flex items-center gap-3">
                        <item.icon className="w-4 h-4 text-slate-400" />
                        <span className="text-white text-sm">{item.label}</span>
                      </div>
                      <span className={`text-xs font-medium ${i < 2 ? 'text-emerald-400' : 'text-amber-400'}`}>{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="order-1 md:order-2"
            >
              <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-4">
                <WifiOff className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">
                  {t('mobile_offline_badge') || 'Offline Mode'}
                </span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                {t('mobile_offline_title') || 'No Internet? No Problem.'}
              </h2>
              <p className="text-slate-400 mb-6">
                {t('mobile_offline_desc') || 'VendFactura keeps working even without an internet connection. Process cash sales, create invoices, and browse your products. Everything syncs automatically when you are back online.'}
              </p>
              <p className="text-slate-500 text-sm">
                {t('mobile_offline_note') || 'Note: Card payments require internet. Email delivery queues until connectivity is restored.'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#0d1321]" data-testid="mobile-faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              {t('mobile_faq_title') || 'Frequently Asked Questions'}
            </h2>
          </motion.div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05} variants={fadeUp}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl px-6 py-4 transition-colors text-left"
                  data-testid={`mobile-faq-${i}`}
                >
                  <span className="text-white font-medium text-sm pr-4">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="overflow-hidden">
                    <div className="px-6 py-4 text-slate-400 text-sm leading-relaxed">
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('mobile_cta_title') || 'Ready to Go Mobile?'}
            </h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              {t('mobile_cta_desc') || 'Install VendFactura on your device now or download the complete guide for step-by-step instructions.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={deferredPrompt ? handleInstall : () => {
                  document.getElementById('install-guide')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3.5 rounded-lg font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-emerald-500/25"
                data-testid="mobile-install-btn-bottom"
              >
                <Download className="w-5 h-5" />
                {deferredPrompt
                  ? (t('mobile_install_btn') || 'Install Now')
                  : (t('mobile_install_guide_btn') || 'How to Install')}
              </button>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 px-4 py-3.5 font-semibold transition-colors"
              >
                {t('mobile_cta_login') || 'Log In to Your Account'}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobileAppPage;
